// PHOTO CURATION — Phase 1. Pure grouping/rotation helpers over a tournament's
// APPROVED photos, built entirely on the existing per-photo identity that
// moderatePhoto already leaves behind: teamId, timestamp, type. No React, no
// Firebase — unit-testable under `node --test`. Moderation is untouched; this
// is a read/organize layer over items that are already status:'approved'.

const HOUR_MS = 3_600_000
const GALLERY = '__gallery__' // internal key for null/guest teamId
const clamp01 = (n) => Math.max(0, Math.min(1, Number(n) || 0))

// Approved photos as a flat array, newest-first. The ONE place the approved
// filter lives.
export function approvedPhotos(photosMap) {
  return Object.entries(photosMap || {})
    .map(([id, v]) => ({ id, ...(v || {}) }))
    .filter((p) => p.status === 'approved')
    .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
}

// teamId → team name, walked from the flights tree (pure).
export function buildTeamNames(flights) {
  const map = {}
  for (const f of Object.values(flights || {})) {
    for (const [tid, t] of Object.entries(f?.teams || {})) {
      map[tid] = t?.info?.name || tid
    }
  }
  return map
}

// Group by team. Only teams WITH uploads appear; guest/null-team photos fall
// into a single "Gallery" bucket (teamId:null). Named teams first by newest
// cover, Gallery last.
export function groupByTeam(photos, teamNames = {}) {
  const buckets = new Map()
  for (const p of photos || []) {
    const key = p.teamId || GALLERY
    if (!buckets.has(key)) buckets.set(key, [])
    buckets.get(key).push(p)
  }
  const out = []
  for (const [key, list] of buckets) {
    const sorted = [...list].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
    out.push({
      teamId: key === GALLERY ? null : key,
      teamName: key === GALLERY ? 'Gallery' : (teamNames[key] || key),
      count: sorted.length,
      cover: sorted[0] || null,
      photos: sorted,
    })
  }
  out.sort((a, b) => {
    if (a.teamId === null) return 1          // Gallery always last
    if (b.teamId === null) return -1
    return (b.cover?.timestamp || 0) - (a.cover?.timestamp || 0)
  })
  return out
}

function hourLabel(start, blockMs) {
  const opt = { hour: 'numeric', minute: '2-digit' }
  const a = new Date(start).toLocaleTimeString([], opt)
  const b = new Date(start + blockMs).toLocaleTimeString([], opt)
  return `${a} – ${b}`
}

// Group by time into fixed blocks (hourly by default), newest block first.
// waveLabeler(blockStart) may return a soft "Wave" label layered on top of the
// hourly bucket (buckets stay hourly regardless).
export function groupByTime(photos, { blockMs = HOUR_MS, waveLabeler = null } = {}) {
  const buckets = new Map()
  for (const p of photos || []) {
    const ts = Number(p.timestamp) || 0
    const start = Math.floor(ts / blockMs) * blockMs
    if (!buckets.has(start)) buckets.set(start, [])
    buckets.get(start).push(p)
  }
  return [...buckets.keys()]
    .sort((a, b) => b - a)
    .map((start) => {
      const list = buckets.get(start).sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
      return {
        start,
        label: hourLabel(start, blockMs),
        waveLabel: waveLabeler ? (waveLabeler(start) || null) : null,
        count: list.length,
        cover: list[0] || null,
        // AI winners of the hour: best still + best clip (either may be null).
        topPhoto: bestByInterest(list.filter((p) => p.type !== 'video')),
        topVideo: bestByInterest(list.filter((p) => p.type === 'video')),
        photos: list,
      }
    })
}

export function videosOf(photos) {
  return (photos || []).filter((p) => p.type === 'video')
}

// Smart TV playlist: recency-biased, but no more than `teamCap` consecutive
// slides from the same team while another team still has pending items — so a
// single team's burst can't take over the wall. Every photo is included; the
// only time the cap is exceeded is when one team is the ONLY source left.
// PHASE 3 adds two optional knobs (defaults off ⇒ Phase 1/2 behavior):
//   dedupWindowMs — collapse a submitter's rapid burst to one representative
//                   so 5 identical shots don't each get a slide.
//   burstWindowMs + burstMax + now — meter a live burst: within burstWindowMs
//                   of `now`, one submitter contributes at most burstMax "hot"
//                   slides; the rest are demoted to the back so the burst drips
//                   in over time instead of flooding the wall.
export function buildTvRotation(photos, {
  teamCap = 2, now = 0, burstWindowMs = 0, burstMax = Infinity, dedupWindowMs = 0,
} = {}) {
  let items = [...(photos || [])]
  if (items.length <= 1) return items
  if (dedupWindowMs > 0) items = collapseDuplicates(items, { windowMs: dedupWindowMs })

  let hot = items
  let cold = []
  if (now > 0 && burstWindowMs > 0 && Number.isFinite(burstMax)) {
    hot = []; cold = []
    const seen = new Map()
    for (const p of [...items].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))) {
      const inWindow = (now - (Number(p.timestamp) || 0)) <= burstWindowMs
      if (!inWindow) { hot.push(p); continue }
      const who = `${p.submittedBy || '?'}|${p.teamId || ''}`
      const n = seen.get(who) || 0
      if (n < burstMax) { hot.push(p); seen.set(who, n + 1) } else cold.push(p)
    }
  }
  return [...roundRobin(hot, teamCap), ...roundRobin(cold, teamCap)]
}

// Team round-robin with a consecutive cap (the Phase 1 core, extracted).
function roundRobin(items, teamCap) {
  if (items.length <= 1) return [...items]
  const queues = new Map()
  for (const p of items) {
    const k = p.teamId || GALLERY
    if (!queues.has(k)) queues.set(k, [])
    queues.get(k).push(p)
  }
  for (const q of queues.values()) q.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  const out = []
  let lastKey = null
  let sameCount = 0
  while (out.length < items.length) {
    let live = [...queues.entries()].filter(([, q]) => q.length > 0)
    if (live.length === 0) break
    if (lastKey && sameCount >= teamCap) {
      const others = live.filter(([k]) => k !== lastKey)
      if (others.length > 0) live = others
    }
    live.sort((a, b) =>
      (b[1][0].timestamp || 0) - (a[1][0].timestamp || 0) || (a[0] < b[0] ? -1 : 1))
    const [key, q] = live[0]
    out.push(q.shift())
    if (key === lastKey) sameCount++
    else { lastKey = key; sameCount = 1 }
  }
  return out
}

// ── PHASE 3: source classification + duplicate collapse ────────────────────
// staff = photo-admin / super-admin post; player = a scorecard submission
// (carries teamId); guest = anonymous public with no team.
export function sourceOf(p) {
  const role = p?.submitterRole
  if (role === 'admin' || role === 'photoAdmin') return 'staff'
  if (p?.teamId) return 'player'
  return 'guest'
}

export function filterBySource(photos, source) {
  if (!source || source === 'all') return photos || []
  return (photos || []).filter((p) => sourceOf(p) === source)
}

// Collapse a submitter's rapid burst (same submittedBy + teamId, within
// windowMs) into one representative carrying { dupCount, dupes }. First pass
// (submitter + tight time window); perceptual hashing can refine it later.
export function collapseDuplicates(photos, { windowMs = 0 } = {}) {
  const list = [...(photos || [])].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  if (windowMs <= 0) return list.map((p) => ({ ...p, dupCount: 1, dupes: [] }))
  const out = []
  let i = 0
  while (i < list.length) {
    const rep = list[i]
    const dupes = []
    let j = i + 1
    while (j < list.length) {
      const same = !!rep.submittedBy
        && (list[j].submittedBy || '') === (rep.submittedBy || '')
        && (list[j].teamId || '') === (rep.teamId || '')
      const within = ((Number(rep.timestamp) || 0) - (Number(list[j].timestamp) || 0)) <= windowMs
      if (same && within) { dupes.push(list[j]); j++ } else break
    }
    out.push({ ...rep, dupCount: 1 + dupes.length, dupes })
    i = j
  }
  return out
}

// Stable, uppercase "find this" locator from a photoId (shown on TV labels
// alongside the scannable permalink QR).
export function shortCode(id) {
  const cleaned = String(id || '').replace(/[^a-zA-Z0-9]/g, '')
  return (cleaned.slice(-4) || '0000').toUpperCase()
}

// ── PHASE 2: highlights ranking ────────────────────────────────────────────
// Time decay: 1 at age 0, halving every halfLifeMs. Monotonically decreasing.
const HALF_LIFE_MS = 6 * HOUR_MS
export function decay(ageMs, { halfLifeMs = HALF_LIFE_MS } = {}) {
  const age = Math.max(0, Number(ageMs) || 0)
  return 1 / (1 + age / halfLifeMs)
}

// Highlights — a PURELY SHARED ranking (same for everyone): likes + admin
// featured, time-decayed so recent-and-loved beats old-and-loved. NOT
// personalized by a viewer's favorites (favorites are a separate device-local
// tab). With no likes and nothing featured, every score is 0 → the result
// falls back to pure recency (newest first). `now` is injected for determinism.
export function rankHighlights(photos, { now = 0, weights = {} } = {}) {
  // PHASE 3: aiInterest is a small SHARED booster (a per-photo field, same for
  // everyone) — likes + featured still lead. Absent ⇒ 0 contribution.
  const { like = 1, featured = 5, interest = 0.5 } = weights
  const scored = (photos || []).map((p) => {
    const base = like * (Number(p.likeCount) || 0)
      + featured * (p.featured ? 1 : 0)
      + interest * clamp01(Number(p.aiInterest) || 0)
    const score = base * decay(now - (Number(p.timestamp) || 0))
    return { p, score, ts: Number(p.timestamp) || 0 }
  })
  scored.sort((a, b) => (b.score - a.score) || (b.ts - a.ts)) // tie-break newest
  return scored.map((s) => s.p)
}

// AI winner — the single highest-interest item in a set, using the existing
// per-photo `aiInterest` (0..1) that moderatePhoto leaves behind. No voting:
// crowned purely by the AI score; ties (or a set with no AI scores) break to
// newer. Returns null for an empty set. Each hourly block crowns TWO winners
// via groupByTime: block.topPhoto (best still) and block.topVideo (best clip).
export function bestByInterest(items) {
  let best = null
  let bestScore = -1
  let bestTs = -1
  for (const p of items || []) {
    const score = clamp01(Number(p.aiInterest) || 0)
    const ts = Number(p.timestamp) || 0
    if (score > bestScore || (score === bestScore && ts > bestTs)) {
      best = p; bestScore = score; bestTs = ts
    }
  }
  return best
}

// Rolling TV window: the photos uploaded within the last `windowMs` of `now`,
// newest-first. NOT a hard clock-hour cutoff — a sliding window, so a photo
// uploaded late in an hour keeps showing well into the next stretch (pick
// windowMs > 1h) and nobody gets shortchanged at the top of the hour. If fewer
// than `minCount` fall in the window, backfill with the next-newest older
// photos so the wall never starves. now<=0 or windowMs<=0 ⇒ pass-through (all
// photos, newest-first).
export function recentWindow(photos, { now = 0, windowMs = 0, minCount = 0 } = {}) {
  const sorted = [...(photos || [])].sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0))
  if (!(now > 0) || !(windowMs > 0)) return sorted
  const cutoff = now - windowMs
  const inWindow = sorted.filter((p) => (Number(p.timestamp) || 0) >= cutoff)
  if (inWindow.length >= minCount) return inWindow
  return sorted.slice(0, Math.max(minCount, inWindow.length))
}

// Permanent share link for a single photo (the TV "Find this" QR + lightbox
// Share both point here).
export function photoPermalink(tournamentId, photoId) {
  return `/photo?t=${encodeURIComponent(tournamentId || '')}&photo=${encodeURIComponent(photoId || '')}`
}
